import { Reference } from '@apollo/client';
import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useDeleteCommunityReplyMutation } from 'generated/graphql';
import { CommunityId } from '../model/community.types';
import { PostId } from '../model/post.types';
import { ReplyId } from '../model/reply.types';
import { modifyPostCommentCount } from '../lib/modifyPostCommentCount';

type DeleteReplyArgs = {
  communityId: CommunityId;
  postId: PostId | null;
  replyId: ReplyId;
};

export const useDeleteReply = () => {
  const [deleteReplyMutation] = useDeleteCommunityReplyMutation();

  return useCallback(
    ({ communityId, postId, replyId }: DeleteReplyArgs): Promise<boolean> =>
      deleteReplyMutation({
        variables: { communityId, replyId },
        optimisticResponse: {
          __typename: 'Mutation',
          deleteReply: { __typename: 'Reply', id: replyId },
        },
        update: (cache) => {
          // cache.modify without an id would edit ROOT_QUERY
          if (postId === null) return;
          cache.modify({
            id: cache.identify({ __typename: 'Post', id: postId }),
            fields: {
              replies: (existing: readonly Reference[] = [], { readField }) =>
                existing.filter((ref) => readField('id', ref) !== replyId),
            },
          });
          modifyPostCommentCount(cache, postId, -1);
        },
      })
        .then(() => {
          toast.success('Your comment was successfully deleted!');
          return true;
        })
        .catch(() => {
          toast.error('Something went wrong');
          return false;
        }),
    [deleteReplyMutation],
  );
};
